import React, { useContext, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useTwcg2304ImportInOnboarding } from '@confluence/experiment-twcg-2304-import-in-onboarding/entry-points/useTwcg2304ImportInOnboarding.ts';

import { ImportModalTypes } from '../Modals/ImportModalTypes.ts';
import { ProgressStatus } from '../Modals/ProgressModal/progress-types.ts';
import { useValidateBulkDocuments } from '../../hooks/useValidateBulkDocuments.ts';
import { useHandleRejectedDocuments } from '../../hooks/useHandleRejectedDocuments.ts';
import { useHandleBulkProcessingErrors } from '../../hooks/useHandleBulkProcessingErrors.ts';
import { useAddAbortControllerDocuments } from '../../hooks/useAddAbortControllerDocuments.ts';
import { DocErrors, ErrorRoles } from '../../hooks/useUploadReducer.ts';
import { createParentPage, deletePage } from '../../services/DocImportService.ts';
import { checkIsAllDocsNotLoaded } from '../../utils/checkIsAllDocsNotLoaded.ts';
import { REQUEST_ABORTED } from '../../utils/cfetchWithAbort.ts';
import { i18n } from '../Modals/CommonErrorModal.tsx';
import { i18n as bulkLimitModalText } from '../Modals/BulkLimitModal.tsx';
import { useHandleDeleteParentPage } from '../../hooks/useHandleDeleteParentPage.ts';
import { ImportStateContext } from '../ImportStateContextProvider.tsx';
import { parentPageTypes } from '../../utils/getRestrictionType.ts';
import { useShowRestrictionWarningModal } from '../../hooks/useShowRestrictionWarningModal.ts';
import { useBulkLimitCheck } from '../../hooks/useBulkLimitCheck.ts';
import type { ImportStrategyType, UploadStrategyType } from '../types.ts';
import { handleBulkExport } from '../../utils/handleExportedArray.ts';
import { MarkdownImportError } from '../../services/MarkdownImportService.ts';
import type { MarkdownWarning } from '../../utils/normalizeMarkdownAdf.ts';
import { getFileNameWithoutExtension } from '../../utils/getFileNameWithoutExtension.ts';

const aggregateWarnings = (warnings: MarkdownWarning[]): MarkdownWarning[] => {
	const warningCounts = new Map<MarkdownWarning['code'], number>();
	warnings.forEach(({ code, count }) => {
		warningCounts.set(code, (warningCounts.get(code) ?? 0) + count);
	});

	return Array.from(warningCounts, ([code, count]) => ({ code, count }));
};

const isExpectedAbort = (error: unknown): boolean =>
	error === REQUEST_ABORTED || (error instanceof Error && Reflect.get(error, 'code') === 'aborted');

export const useUploadBulk = (
	setIsCancelledDocumentsDeleted: any,
	currentSpace: any,
	restrictionType: any,
	parentPageId: any,
	handleUploadFileWithDiscard: any,
	setImportedPageData: any,
	importStrategy: React.MutableRefObject<React.MutableRefObject<ImportStrategyType> | null>,
	subType: any,
	deleteOnboardingBlankPage?: () => Promise<void>,
	spaceId?: string | null,
	hasSourceRestrictions = false,
): UploadStrategyType => {
	const {
		showModal,
		closeModal,
		updateModalData,
		docs,
		docActions: { uploadDocFailure, uploadDocSuccess },
	} = useContext(ImportStateContext);
	const [bulkError, setBulkError] = useState<string | null>(null);
	const validateBulkDocuments = useValidateBulkDocuments();
	const handleRejectedDocuments = useHandleRejectedDocuments(setBulkError);
	const handleBulkProcessingErrors = useHandleBulkProcessingErrors();
	const addAbortControllerDocuments = useAddAbortControllerDocuments();
	const [bulkParentPageId, setBulkParentPageId] = useState<string | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const showRestrictionWarningModal = useShowRestrictionWarningModal();
	const bulkLimitCheck = useBulkLimitCheck();
	const { isEnabled: isUploadInOnboardingEnabled, loading: isUploadInOnboardingLoading } =
		useTwcg2304ImportInOnboarding();
	const isExperimentActive = !isUploadInOnboardingLoading && isUploadInOnboardingEnabled;
	const isIndependentPagesImport =
		importStrategy.current?.current?.bulkImportMode === 'independent-pages';
	useHandleDeleteParentPage(bulkError, bulkParentPageId, setBulkParentPageId);

	//TODO: WTF?
	const docsRef = useRef(docs);
	docsRef.current = docs;

	const handleDeleteCancelledDocuments = () => {
		setIsCancelledDocumentsDeleted(false);
		const cancelledDocuments = docsRef.current.filter(
			(doc) => doc.error?.type === DocErrors.REQUEST_ABORTED_ERROR && doc.id,
		);

		try {
			cancelledDocuments.forEach(async (cancelledDoc) => {
				await deletePage(cancelledDoc.id, { isPublicPage: true });
			});
		} catch (e) {
			return;
		}
		setIsCancelledDocumentsDeleted(true);
		checkIsAllDocsNotLoaded(docsRef.current, setBulkError);
	};

	const handleCreateParentPage = async () => {
		try {
			const folderName = isExperimentActive
				? importStrategy.current?.current?.folderName
				: undefined;
			const effectiveRestrictionType = isExperimentActive ? 'free' : restrictionType;
			const createParentPageResult = folderName
				? await createParentPage(
						currentSpace,
						parentPageId,
						effectiveRestrictionType,
						subType,
						folderName,
						true,
					)
				: await createParentPage(currentSpace, parentPageId, effectiveRestrictionType, subType);
			const {
				data: { id },
			} = createParentPageResult;
			setBulkParentPageId(id);
			return id;
		} catch (err) {
			if (err === REQUEST_ABORTED) return;
			closeModal(ImportModalTypes.PROGRESS);

			//TODO: refactor: create a separate modal
			showModal(ImportModalTypes.UPLOAD_ERROR, {
				headText: <FormattedMessage {...i18n.failedBulkImportPopupTitle} />,
				bodyText: <FormattedMessage {...i18n.failedBulkImportPopupContent} />,
				submitText: <FormattedMessage {...bulkLimitModalText.bulkLimitModalDialogRetryAction} />,
				modalWidth: 'small',
			});
		}
	};

	const sendBulkAnalytics = (actionSubjectId, attributes = {}) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'IFSBulkUpload',
				action: 'clicked',
				actionSubject: 'bulkUploads',
				actionSubjectId,
				attributes: {
					...attributes,
				},
			},
		}).fire();
	};

	const validateDocuments = (documents: (File | null)[]): File[] | [] | false => {
		const validatedDocuments = validateBulkDocuments(documents);
		if (!validatedDocuments.length) {
			updateModalData(ImportModalTypes.PROGRESS, {
				status: ProgressStatus.ERROR,
			});
			return false;
		}

		return validatedDocuments;
	};

	const handleIndependentPagesUpload = async (
		documents: { doc: File; signal: AbortSignal }[],
	): Promise<void> => {
		const strategy = importStrategy.current?.current;
		const uploadSingle = strategy?.uploadSingle;
		if (!uploadSingle || !spaceId || hasSourceRestrictions) {
			documents.forEach(({ doc }) => {
				uploadDocFailure(doc.name, {
					type: DocErrors.FAILED_UPLOAD_ERROR,
					role: ErrorRoles.ERROR,
				});
			});
			updateModalData(ImportModalTypes.PROGRESS, { status: ProgressStatus.ERROR });
			strategy?.onImportCompleted?.({
				mode: 'bulk',
				result: 'create_failure',
				selectedFileCount: documents.length,
				successfulFileCount: 0,
				failedFileCount: documents.length,
				errorCode: !spaceId || hasSourceRestrictions ? 'invalid_destination' : 'unknown',
			});
			return;
		}

		let successfulUploads = 0;
		let failedUploads = 0;
		let firstErrorCode: 'unknown' | MarkdownImportError['code'] | undefined;
		const warnings: MarkdownWarning[] = [];
		const resolveParentPage = importStrategy.current?.current?.resolveParentPage;
		const resolvedParentPageId =
			resolveParentPage && documents[0]
				? await resolveParentPage({
						spaceId,
						spaceKey: currentSpace,
						parentPageId,
						signal: documents[0].signal,
					})
				: parentPageId;

		for (const { doc, signal } of documents) {
			try {
				const {
					data,
					errors,
					warnings: documentWarnings = [],
				} = await uploadSingle(doc, {
					spaceId,
					spaceKey: currentSpace,
					parentPageId: resolvedParentPageId,
					signal,
					onCreateDispatch: () =>
						updateModalData(ImportModalTypes.PROGRESS, { cancelDisabled: true }),
				});
				const documentTitle = getFileNameWithoutExtension(doc.name);
				if (errors) {
					handleBulkProcessingErrors(documentTitle, errors);
				}
				uploadDocSuccess(doc.name, data.id);
				if (successfulUploads === 0) {
					setImportedPageData({ id: data.id, space: currentSpace });
				}
				successfulUploads += 1;
				warnings.push(...documentWarnings);
			} catch (error) {
				if (isExpectedAbort(error)) {
					strategy.onImportCompleted?.({
						mode: 'bulk',
						result: 'cancelled',
						selectedFileCount: documents.length,
						successfulFileCount: successfulUploads,
						failedFileCount: failedUploads,
						warnings,
						errorCode: 'aborted',
					});
					return;
				}

				if (error instanceof MarkdownImportError && error.code === 'outcome_unknown') {
					strategy.onImportCompleted?.({
						mode: 'bulk',
						result: 'outcome_unknown',
						selectedFileCount: documents.length,
						successfulFileCount: successfulUploads,
						failedFileCount: failedUploads,
						warnings,
						errorCode: 'outcome_unknown',
					});
					closeModal(ImportModalTypes.PROGRESS);
					showModal(ImportModalTypes.OUTCOME_UNKNOWN, { attemptId: error.attemptId });
					return;
				}

				failedUploads += 1;
				firstErrorCode ??= error instanceof MarkdownImportError ? error.code : 'unknown';
				uploadDocFailure(doc.name, {
					type: DocErrors.FAILED_UPLOAD_ERROR,
					role: ErrorRoles.ERROR,
				});
			}
		}

		const aggregatedWarnings = aggregateWarnings(warnings);
		const status = successfulUploads > 0 ? ProgressStatus.SUCCESS : ProgressStatus.ERROR;
		strategy.onImportCompleted?.({
			mode: 'bulk',
			result:
				successfulUploads === documents.length
					? 'success'
					: successfulUploads > 0
						? 'partial_success'
						: 'create_failure',
			selectedFileCount: documents.length,
			successfulFileCount: successfulUploads,
			failedFileCount: failedUploads,
			warnings: aggregatedWarnings,
			...(firstErrorCode ? { errorCode: firstErrorCode } : {}),
		});
		updateModalData(ImportModalTypes.PROGRESS, {
			status,
			...(status === ProgressStatus.ERROR ? { cancelDisabled: false } : {}),
			...(aggregatedWarnings.length ? { warnings: aggregatedWarnings } : {}),
		});
	};

	const handleUpload = async (documents: (File | null)[]) => {
		const importStrategyRef = importStrategy.current;
		//TODO: move it
		setBulkError(null);

		const validatedDocuments = isIndependentPagesImport
			? documents.filter((document): document is File => document !== null)
			: validateDocuments(documents);
		if (!validatedDocuments) return;
		const validatedDocumentsWithAbortSignal = addAbortControllerDocuments(validatedDocuments);

		try {
			sendBulkAnalytics(`${importStrategyRef!.current.key}BulkUpload`, {
				documentsCount: documents.length,
			});
			if (isIndependentPagesImport) {
				await handleIndependentPagesUpload(validatedDocumentsWithAbortSignal);
				return;
			}
			const createdBulkParentPageId = await handleCreateParentPage();
			if (!createdBulkParentPageId) return false;
			setImportedPageData({ id: createdBulkParentPageId, space: currentSpace });
			//TODO: move into a function
			const response = await Promise.allSettled(
				validatedDocumentsWithAbortSignal
					.map(async ({ doc, signal }: { doc: File; signal: AbortSignal }) => {
						const {
							data: { title, id },
							errors,
						} = await handleUploadFileWithDiscard(doc, {
							createdBulkParentPageId,
							isBulkUpload: true,
							signal,
						});
						if (errors) {
							handleBulkProcessingErrors(title, errors);
						}
						uploadDocSuccess(title, id);
						return id;
					})
					.filter((id: Promise<string | undefined>) => id),
			);

			await handleRejectedDocuments(response, validatedDocuments);
			void handleDeleteCancelledDocuments();

			// When the experiment created a separate parent page for the bulk import, the blank page
			// originally created (e.g. via handleImportCTAClick) is not replaced by the normal discard
			// mechanism. Delete it explicitly.
			if (isExperimentActive && createdBulkParentPageId && deleteOnboardingBlankPage) {
				void deleteOnboardingBlankPage();
			}

			// When the experiment is active, navigate to the first successfully uploaded child page
			// instead of the parent folder so the user lands on actual imported content.
			if (isExperimentActive) {
				const firstFulfilledId = response.find(
					(r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled' && !!r.value,
				)?.value;
				if (firstFulfilledId) {
					setImportedPageData({ id: firstFulfilledId, space: currentSpace });
				}
			}

			updateModalData(ImportModalTypes.PROGRESS, {
				status: ProgressStatus.SUCCESS,
			});
		} catch (error) {
			if (isIndependentPagesImport) {
				importStrategy.current?.current?.onImportCompleted?.({
					mode: 'bulk',
					result: isExpectedAbort(error) ? 'cancelled' : 'create_failure',
					selectedFileCount: validatedDocuments.length,
					successfulFileCount: 0,
					failedFileCount: isExpectedAbort(error) ? 0 : validatedDocuments.length,
					errorCode:
						error instanceof MarkdownImportError
							? error.code
							: isExpectedAbort(error)
								? 'aborted'
								: 'unknown',
				});
			}
			updateModalData(ImportModalTypes.PROGRESS, {
				status: ProgressStatus.ERROR,
			});
		}
	};

	const restrictionCheck = async (): Promise<boolean> => {
		if (restrictionType !== parentPageTypes.RESTRICTED && !isExperimentActive) {
			return showRestrictionWarningModal();
		}

		return true;
	};

	const validateBeforeProgress = async (documents) => {
		// don't use destructuring on the top of the file to keep the link to the original strategy
		const importStrategyRef = importStrategy.current;
		if (!(await restrictionCheck())) return false;

		const documentsWithinBulkLimits = isIndependentPagesImport
			? await bulkLimitCheck(documents)
			: documents;
		if (!documentsWithinBulkLimits) return false;

		if (importStrategyRef!.current.validateBulkUploadBeforeProgress) {
			return importStrategyRef!.current.validateBulkUploadBeforeProgress(documentsWithinBulkLimits);
		}

		return bulkLimitCheck(documents);
	};

	const handleFilesBeforeUpload = async (documents) => {
		// don't use destructuring on the top of the file to keep the link to the original setrategy
		const importStrategyRef = importStrategy.current;

		if (importStrategyRef!.current.exportFile) {
			return handleBulkExport(documents, importStrategyRef!.current.exportFile);
		}

		return documents;
	};

	const validateBeforeUpload = async (documents) => {
		// don't use destructuring on the top of the file to keep the link to the original strategy
		const importStrategyRef = importStrategy.current;

		if (importStrategyRef!.current.validateBulkBeforeUpload) {
			return importStrategyRef!.current.validateBulkBeforeUpload(documents);
		}

		return validateDocuments(documents);
	};

	return {
		handleUpload,
		validateBeforeProgress,
		handleFilesBeforeUpload,
		validateBeforeUpload,
	};
};
